import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, X, Star } from 'lucide-react';
import { newReleases } from '../data/mock';

const VideoCard = ({ video, onPlay, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Fallback thumbnail if YouTube thumbnail fails
  const fallbackThumbnail = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;

  return (
    <div
      className={`relative overflow-hidden cursor-pointer group ${
        featured ? 'aspect-[16/9]' : 'aspect-video'
      } bg-zinc-900`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onPlay(video)}
    >
      {/* Show Reel Badge */}
      {video.isShowReel && (
        <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 border border-white/20">
          <Star size={12} className="text-yellow-400" fill="currentColor" />
          <span className="text-[10px] tracking-[0.2em] text-white uppercase">Show Reel</span>
        </div>
      )}
      
      <img
        src={imgError ? fallbackThumbnail : video.thumbnail}
        alt={video.title}
        className={`w-full h-full object-cover transition-all duration-700 ${
          isHovered ? 'scale-105' : 'scale-100'
        }`}
        onError={() => setImgError(true)}
      />
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
          isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
      >
        <div className={`${featured ? 'w-20 h-20' : 'w-16 h-16'} rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 group/btn`}>
          <Play size={featured ? 28 : 24} className="ml-1 text-white group-hover/btn:text-black" fill="currentColor" />
        </div>
      </div>
      <div
        className={`absolute bottom-0 left-0 right-0 p-4 ${featured ? 'p-6' : 'p-4'} transition-all duration-500 ${
          isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        <p className={`text-white ${featured ? 'text-lg' : 'text-sm'} font-medium`}>{video.title}</p>
        {video.isShowReel && (
          <p className="text-white/60 text-xs mt-1">Featured Production Showcase</p>
        )}
      </div>
    </div>
  );
};

const VideoModal = ({ video, onClose }) => {
  const [embedError, setEmbedError] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleOpenYouTube = () => {
    window.open(`https://www.youtube.com/watch?v=${video.videoId}`, '_blank');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={onClose}
    >
      <button
        className="absolute top-6 right-6 text-white hover:opacity-60 transition-opacity z-10"
        onClick={onClose}
        aria-label="Close video"
      >
        <X size={32} />
      </button>
      <div
        className="w-full max-w-6xl aspect-video mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`}
          title={video.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        {/* Fallback button to open on YouTube */}
        <div className="absolute bottom-4 right-4">
          <button
            onClick={handleOpenYouTube}
            className="bg-white/10 backdrop-blur-sm text-white text-xs px-4 py-2 hover:bg-white/20 transition-colors flex items-center gap-2"
          >
            <span>Open on YouTube</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const NewReleasesSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Separate show reel from other releases
  const showReel = newReleases.find(v => v.isShowReel);
  const otherReleases = newReleases.filter(v => !v.isShowReel);

  return (
    <>
      <section
        ref={sectionRef}
        className="min-h-screen bg-black py-24 md:py-32 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-[1800px] mx-auto">
          {/* Section Label */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h2 className="text-white text-sm tracking-[0.3em] font-bold mb-12">
              NEW RELEASES
            </h2>
          </div>

          {/* Featured Show Reel */}
          {showReel && (
            <div
              className={`mb-8 transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
            >
              <VideoCard video={showReel} onPlay={setActiveVideo} featured />
            </div>
          )}

          {/* Other Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {otherReleases.map((video, index) => (
              <div
                key={video.id}
                className={`transition-all duration-700 ${
                  isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-12 opacity-0'
                }`}
                style={{ transitionDelay: `${200 + index * 150}ms` }}
              >
                <VideoCard video={video} onPlay={setActiveVideo} />
              </div>
            ))}
          </div>

          {/* Watch More Button */}
          <div
            className={`mt-16 flex justify-center transition-all duration-700 delay-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <Link
              to="/work"
              className="border border-white text-white px-16 py-4 text-xs tracking-[0.3em] hover:bg-white hover:text-black transition-all duration-500"
            >
              WATCH MORE
            </Link>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </>
  );
};

export default NewReleasesSection;
